 //NOTES
//reload logic...
//1. If no session ID on first use // check !currentSessionId && !hasReloaded, Reload iframe once, trigger new session ID and write to local storage, import local value on fresh load
//2. Session expired (404 on POST)// check POST returns 404 with Session not found, Reload iframe once, trigger new session ID
//3 Automatic retry after recovery, Session received + retryUrl present, retry POST once

import { apiUrl } from "./api";

// --- Reader Mode helpers (always defined; TopNav sets default true on first load) ---
const READER_MODE_KEY = "newsCompare:readerMode";
export function getReaderMode() {
  const raw = localStorage.getItem(READER_MODE_KEY);
  if (raw === null || raw === undefined) return false; // if missing, TopNav seeds TRUE; elsewhere we default safe-false
  try {
    return JSON.parse(raw) === true;
  } catch {
    return String(raw).toLowerCase() === "true";
  }
}
export function setReaderMode(val) {
  const v = !!val;
  localStorage.setItem(READER_MODE_KEY, JSON.stringify(v));
  return v;
}

// --- Session state ---
let currentSessionId =
  localStorage.getItem("newsCompare:currentSessionId") || null;
let retryUrl = null;
let hasReloaded = false;

// Public: initialize and listen for session info from the iframe
export function initializeWebfuseSession(setSessionId, isReaderMode) {
  const handler = (event) => {
    if (event.origin !== "https://webfuse.com") return;
    const { params } = event.data || {};
    const msg = params?.msg;
    const receivedSessionId = params?.sessionInfo?.sessionId;

    if (msg === "get_session_info") {
      if (receivedSessionId) {
        if (currentSessionId !== receivedSessionId) {
          console.log(
            "New Session ID ",
            receivedSessionId,
            " Existing Session ID ",
            currentSessionId
          );
          console.warn("PGApp must update session ID");
          currentSessionId = receivedSessionId;
          localStorage.setItem(
            "newsCompare:currentSessionId",
            currentSessionId
          );

          if (typeof setSessionId === "function") {
            console.log(
              "SessionId updated to ",
              currentSessionId,
              "Refreshing session."
            );
            // Attempt to resend the last article with current reader_mode
            resendLastPostedArticle(currentSessionId, isReaderMode);
            setSessionId(currentSessionId);
          }
          console.log("✅ Session ID received:", currentSessionId);
        }

        if (retryUrl) {
          console.log("↩️ Retrying Webfuse POST with updated session...");
          const urlToRetry = retryUrl;
          retryUrl = null;
          hasReloaded = false;
          handleWebfuse(urlToRetry, isReaderMode);
        }
      } else {
        console.warn("⚠️ get_session_info received, but no sessionId found:", params);
      }
    }
  };

  window.addEventListener("message", handler);
  return () => window.removeEventListener("message", handler);
}

// Send a URL to Webfuse; always include reader_mode
export async function handleWebfuse(url, readerModeParam) {
  // Clear any stale retry state if session is valid
  if (currentSessionId && retryUrl) {
    retryUrl = null;
    hasReloaded = false;
  }

  if (!currentSessionId) {
    if (hasReloaded === false) {
      console.warn("⛔ No session ID available. Reloading iframe...");
      retryUrl = url;
      hasReloaded = true;
      window.postMessage({ type: "reload_webfuse_iframe" }, "*");
    } else {
      console.warn("⚠️ Session still unavailable after reload. Aborting.");
    }
    return;
  }

  // Persist last posted
  localStorage.setItem(
    "newsCompare:lastPostedArticle",
    JSON.stringify({ url, posted_at: Date.now() })
  );
  currentSessionId = localStorage.getItem("newsCompare:currentSessionId") || null;

  const readerMode =
    typeof readerModeParam !== "undefined" ? !!readerModeParam : getReaderMode();

  const payload = {
    sessionId: currentSessionId,
    url,
    reader_mode: readerMode,
  };

  console.log('FLOW LOG: 2️⃣ App→Webfuse: enqueue open_url', payload);

  try {
    const endpoint = apiUrl("/api/webfuse-url");
    const req = fetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    console.log('FLOW LOG: 3️⃣ App→Webfuse: POST dispatched', { endpoint });
    window.dispatchEvent(
      new CustomEvent('newscompare:article-submitted', {
        detail: { url, ts: Date.now() }
      })
    );

    const res = await req;

    if (!res.ok) {
      const errorText = await res.text().catch(() => "");
      let errorObj;
      try {
        errorObj = JSON.parse(errorText);
      } catch {
        errorObj = {};
      }
      console.error("❌ Webfuse POST failed:", res.status, errorText);

      // 500 from backend server, 404 from initial Webfuse response; attempt single reload
      if (
        res.status === 500 &&
        errorObj.status === 404 &&
        String(errorObj.text || "").includes("Session not found") &&
        !hasReloaded
      ) {
        retryUrl = url;
        hasReloaded = true;
        console.log("Reloading webfuse iframe");
        window.postMessage({ type: "reload_webfuse_iframe" }, "*");
        currentSessionId =
          localStorage.getItem("newsCompare:currentSessionId") || null;
      }
      return;
    }

    console.log("✅ Article URL sent to Webfuse");
    hasReloaded = false;
  } catch (err) {
    console.error("❌ Webfuse POST failed:", err);
  }
}


// Triggered by get_session_info: resend the last URL; always include reader_mode
export async function resendLastPostedArticle(sessionId, readerModeParam) {
  const lastArticleRaw = localStorage.getItem("newsCompare:lastPostedArticle");
  if (!lastArticleRaw) return;

  try {
    const parsed = JSON.parse(lastArticleRaw);
    if (parsed?.url) {
      // Count this as a fresh post for the “accept first hash after reset” logic
      localStorage.setItem(
        "newsCompare:lastPostedArticle",
        JSON.stringify({ url: parsed.url, posted_at: Date.now() })
      );
      const readerMode =
        typeof readerModeParam !== "undefined"
          ? !!readerModeParam
          : getReaderMode();

      const payload = {
        sessionId,
        url: parsed.url,
        reader_mode: readerMode,
      };
      console.log(
        "📤 Re-sending last article to Webfuse with payload:",
        JSON.stringify(payload, null, 2)
      );

      const res = await fetch(apiUrl("/api/webfuse-url"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error("Failed to post to Webfuse");
      console.log("✅ Article resent to Webfuse after refresh");
    }
  } catch (err) {
    console.warn("⚠️ Could not parse or send lastPostedArticle:", err);
  }
}

// Reset the Webfuse session state; always include reader_mode
export async function resetWebfuseSession(sessionId, readerModeParam) {
  currentSessionId =
    sessionId || localStorage.getItem("newsCompare:currentSessionId") || null;

  try {
    const readerMode =
      typeof readerModeParam !== "undefined"
        ? !!readerModeParam
        : getReaderMode();

    const payload = {
      sessionId: currentSessionId,
      type: "reset_session",
      url: "",
      reader_mode: readerMode,
    };
    console.log("📤 Clearing Webfuse State", JSON.stringify(payload, null, 2));

    const res = await fetch(apiUrl("/api/webfuse-url"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    if (!res.ok) throw new Error("Failed to post to Webfuse");
    console.log("✅ Webfuse reset");
  } catch (err) {
    console.warn("⚠️ Could not reset Webfuse", err);
  }
}

// NEW: update reader mode server-side; always cache and post minimal message
export async function updateReaderMode(readerModeParam) {
  const v = !!readerModeParam;
  setReaderMode(v);
  console.log("🔄 Updating Webfuse reader_mode:", v);

  if (!currentSessionId) {
    console.warn("⚠️ updateReaderMode: No sessionId yet; cached only, will apply on next message.");
    return;
  }

  const payload = {
    sessionId: currentSessionId,
    type: "update_reader_mode",
    url: "",
    reader_mode: v,
  };

  try {
    const res = await fetch(apiUrl("/api/webfuse-url"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      const t = await res.text().catch(() => "");
      console.error("❌ update_reader_mode POST failed:", res.status, t);
    } else {
      console.log("✅ update_reader_mode sent");
    }
  } catch (e) {
    console.error("❌ update_reader_mode error:", e);
  }
}
